<template>
    <div class="rounded-b-[5rem] overflow-hidden min-h-[45vh] max-h-[90vh] relative" role="region"
        aria-label="Homepage Banner Slider">
        <ClientOnly>
            <swiper-container v-if="list.length" class="min-h-[45vh] max-h-[90vh]" v-bind="swiperConfig">
                <swiper-slide v-for="(item, index) in list" :key="item.id || index" class="w-full">
                    <template v-if="item.url">
                        <NuxtLink :to="item.url" class="w-full h-full block">
                            <picture>
                                <source :srcset="getResponsiveImage(item)" media="(max-width: 600px)" />
                                <img
                                    class="w-full min-h-[45vh] max-h-[90vh] object-cover"
                                    :src="getImage(item)"
                                    :alt="item.alt || 'بنر صفحه اصلی نرس‌لند'"
                                    :loading="index === 0 ? 'eager' : 'lazy'"
                                    :fetchpriority="index === 0 ? 'high' : 'auto'"
                                />
                            </picture>
                        </NuxtLink>
                    </template>
                    <template v-else>
                        <picture>
                            <source :srcset="getResponsiveImage(item)" media="(max-width: 600px)" />
                            <img
                                class="w-full min-h-[45vh] max-h-[90vh] object-cover"
                                :src="getImage(item)"
                                :alt="item.alt || 'بنر صفحه اصلی نرس‌لند'"
                                :loading="index === 0 ? 'eager' : 'lazy'"
                                :fetchpriority="index === 0 ? 'high' : 'auto'"
                            />
                        </picture>
                    </template>
                </swiper-slide>
            </swiper-container>
            <template #fallback>
                <!-- SSR First Slide rendered immediately without waiting for client JS hydration -->
                <div v-if="firstBanner" class="w-full min-h-[45vh] max-h-[90vh]">
                    <picture>
                        <source :srcset="firstBannerResponsiveUrl" media="(max-width: 600px)" />
                        <img
                            class="w-full min-h-[45vh] max-h-[90vh] object-cover"
                            :src="firstBannerUrl"
                            :alt="firstBanner.alt || 'بنر صفحه اصلی نرس‌لند'"
                            loading="eager"
                            fetchpriority="high"
                            decoding="async"
                        />
                    </picture>
                </div>
                <div v-else class="min-h-[45vh] max-h-[90vh] bg-sandstone-200 dark:bg-sandstone-800 animate-pulse"></div>
            </template>
        </ClientOnly>
        <div v-if="bannerError" class="text-center text-red-500 mt-4">
            Failed to load banners. Please try again later.
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { IBanner } from "~/libs/types/api/banner";

const imgFormatter = useImage();

const {
    data: banners,
    pending,
    error: bannerError,
} = await useBanners({
    key: "homePage",
    options: {
        params: {
            sort: "order",
        }
    }
});

const list = computed(() => banners.value?.data || []);

const firstBanner = computed(() => list.value[0] || null);
const firstBannerUrl = computed(() => firstBanner.value ? getImage(firstBanner.value) : "");
const firstBannerResponsiveUrl = computed(() => firstBanner.value ? getResponsiveImage(firstBanner.value) : "");

useHead(() => {
    if (!firstBanner.value) return {};
    return {
        link: [
            {
                rel: "preload",
                href: firstBannerResponsiveUrl.value,
                as: "image",
                type: "image/webp",
                media: "(max-width: 600px)",
                fetchpriority: "high",
            },
            {
                rel: "preload",
                href: firstBannerUrl.value,
                as: "image",
                type: "image/webp",
                media: "(min-width: 601px)",
                fetchpriority: "high",
            },
        ],
    };
});

// Swiper configuration as a computed property
const swiperConfig = {
    effect: "coverflow",
    coverflowEffect: {
        depth: 200,
        rotate: 85,
        scale: 1,
        slideShadows: false,
        stretch: 50,
    },
    loop: true,
    navigation: true,
    autoplay: { delay: 8000 },
};

// Helper functions for image URLs
function getImage(item: IBanner) {
    return imgFormatter(item.media.image.original_url, {}, { preset: "hero" });
}

function getResponsiveImage(item: IBanner) {
    return imgFormatter(item.media.responsive_image.original_url, { width: 640 }, { preset: "heroMobile" });
}
</script>